import { useEffect, useMemo, useState } from 'react';
import { customersApi } from '@/api/customers.api';
import type { SelectOption } from '@/components/ui/Select';
import type { CustomerResponse } from '@/types/customer';
import { formatCustomerLabel } from '@/utils/entityLabels';
import { showApiError } from '@/utils/apiError';

export function useCustomerOptions(pageSize = 500) {
  const [customers, setCustomers] = useState<CustomerResponse[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    customersApi
      .list({ page: 0, size: pageSize, activeOnly: true })
      .then((res) => setCustomers(res.content))
      .catch((err) => showApiError(err, 'Failed to load customers'))
      .finally(() => setLoading(false));
  }, [pageSize]);

  const options: SelectOption[] = useMemo(
    () =>
      customers.map((customer) => ({
        value: String(customer.id),
        label: formatCustomerLabel(customer),
      })),
    [customers],
  );

  const byId = useMemo(() => new Map(customers.map((c) => [c.id, c])), [customers]);

  const getCustomer = (id: number | string | null | undefined) => {
    if (id == null || id === '') return undefined;
    return byId.get(Number(id));
  };

  const getLabel = (id: number | string | null | undefined) => {
    const customer = getCustomer(id);
    if (!customer) return id != null && id !== '' ? `#${id}` : '—';
    return formatCustomerLabel(customer);
  };

  return { customers, options, loading, getCustomer, getLabel, byId };
}
